import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { useRefreshPsaSettings } from "../api/useRefreshPsaSettings";
import { ResolvedIntegration } from "../types/PsaProject";
import useStyles from "../styles/useStyles";

interface Props {
  integration: ResolvedIntegration;
  canRefresh: boolean;
}

const ENTITY_LABELS: Record<string, string> = {
  boards: "Boards",
  statuses: "Statuses",
  priorities: "Priorities",
  types: "Types",
  members: "Members",
  templates: "Templates",
};

const RefreshSettings: React.FC<Props> = ({ integration, canRefresh }) => {
  const styles = useStyles();
  const { status, error, refresh } = useRefreshPsaSettings(integration);
  const [toast, setToast] = useState<{
    message: string;
    variant: "success" | "warning";
  } | null>(null);

  useEffect(() => {
    if (status === "refreshed") {
      setToast({ message: "PSA settings refreshed.", variant: "success" });
    } else if (status === "timedOut") {
      setToast({
        message:
          "The PSA settings refresh is still running. Reload this page in a minute to see the results.",
        variant: "warning",
      });
    } else if (status === "failed") {
      setToast({ message: error, variant: "warning" });
    }
  }, [status, error]);

  const { refreshed_at: refreshedAt, reference = {} } = integration.settings;
  const failedEntities = Object.entries(reference).filter(
    ([, entity]) => entity?.ok === false
  );
  // ConnectWise is the only PSA whose project form needs a project board; with
  // none cached the board picker silently disappears from the form.
  const projectBoards = (reference.boards?.items ?? []).filter(
    (board: any) => board.type === "project"
  );
  const missingProjectBoards =
    !!refreshedAt &&
    integration.service === "connectwise" &&
    projectBoards.length === 0;
  const refreshing = status === "refreshing";

  return (
    <div className={styles.refreshSettings}>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          onClose={() => setToast(null)}
          text={toast.message}
        />
      )}
      <div className={styles.refreshRow}>
        <span className={styles.refreshMeta}>
          {refreshedAt
            ? `PSA settings last refreshed ${new Date(
                refreshedAt
              ).toLocaleString()}`
            : "PSA settings have not been loaded yet."}
        </span>
        {canRefresh && (
          <Button
            className="btnSeawhite"
            disabled={refreshing}
            onClick={refresh}
          >
            {refreshing ? "Refreshing…" : "Refresh PSA settings"}
          </Button>
        )}
      </div>
      {missingProjectBoards && (
        <div className={styles.refreshWarning}>
          No ConnectWise project boards were found. Check that your project
          boards are active in ConnectWise and that the integration&rsquo;s API
          member can see them, then refresh PSA settings.
        </div>
      )}
      {failedEntities.length > 0 && (
        <div className={styles.refreshWarning}>
          Some PSA settings could not be loaded:
          <ul>
            {failedEntities.map(([key, entity]) => (
              <li key={key}>
                {ENTITY_LABELS[key] ?? key}: {entity?.error || "Unknown error"}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RefreshSettings;
